import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { discoverRelayList } from '@/lib/outbox';
import { BLOG_OWNER_PUBKEY, BLOG_HASHTAG } from '@/lib/blogConfig';

export interface Article {
  event: NostrEvent;
  identifier: string;
  title: string;
  summary: string;
  image?: string;
  publishedAt: number;
  hashtags: string[];
}

/** Extract the first matching tag value. */
function tagValue(event: NostrEvent, name: string): string | undefined {
  return event.tags.find(([n]) => n === name)?.[1];
}

/** Convert a kind 30023 event into a structured Article. */
export function parseArticle(event: NostrEvent): Article {
  const identifier = tagValue(event, 'd') ?? '';
  const title = tagValue(event, 'title') ?? 'Untitled';
  const summary = tagValue(event, 'summary') ?? '';
  const image = tagValue(event, 'image');
  const publishedRaw = tagValue(event, 'published_at');
  const publishedAt = publishedRaw ? parseInt(publishedRaw, 10) : event.created_at;
  const hashtags = event.tags.filter(([n]) => n === 't').map(([, v]) => v);

  return { event, identifier, title, summary, image, publishedAt, hashtags };
}

/** Whether an article carries the blog's target hashtag (case-insensitive). */
export function hasBlogHashtag(event: NostrEvent): boolean {
  const target = BLOG_HASHTAG.toLowerCase();
  return event.tags.some(
    ([n, v]) => n === 't' && typeof v === 'string' && v.toLowerCase() === target,
  );
}

/**
 * Fetch all of the blog owner's kind 30023 articles tagged with the blog
 * hashtag, pulled from their NIP-65 write (outbox) relays.
 */
export function useArticles() {
  const { nostr } = useNostr();

  return useQuery<Article[]>({
    queryKey: ['articles', BLOG_OWNER_PUBKEY, BLOG_HASHTAG],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);

      // 1. Discover the owner's write relays via the outbox model.
      const relayList = await discoverRelayList(nostr, BLOG_OWNER_PUBKEY, { signal });

      // 2. Query those write relays for the owner's tagged articles.
      const group = nostr.group(relayList.write);
      const events = await group.query(
        [
          {
            kinds: [30023],
            authors: [BLOG_OWNER_PUBKEY],
            '#t': [BLOG_HASHTAG, BLOG_HASHTAG.toLowerCase()],
          },
        ],
        { signal },
      );

      // 3. Deduplicate by `d` identifier, keeping the newest version.
      const byId = new Map<string, NostrEvent>();
      for (const ev of events) {
        if (!hasBlogHashtag(ev)) continue;
        const id = ev.tags.find(([n]) => n === 'd')?.[1] ?? ev.id;
        const existing = byId.get(id);
        if (!existing || ev.created_at > existing.created_at) {
          byId.set(id, ev);
        }
      }

      const articles = [...byId.values()].map(parseArticle);
      articles.sort((a, b) => b.publishedAt - a.publishedAt);
      return articles;
    },
    staleTime: 2 * 60 * 1000,
  });
}

/** Fetch a single article by its `d` identifier from the owner's write relays. */
export function useArticle(identifier: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<Article | null>({
    queryKey: ['article', BLOG_OWNER_PUBKEY, identifier ?? ''],
    queryFn: async (c) => {
      if (!identifier) return null;
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(8000)]);

      const relayList = await discoverRelayList(nostr, BLOG_OWNER_PUBKEY, { signal });
      const group = nostr.group(relayList.write);
      const events = await group.query(
        [
          {
            kinds: [30023],
            authors: [BLOG_OWNER_PUBKEY],
            '#d': [identifier],
          },
        ],
        { signal },
      );

      if (events.length === 0) return null;
      const newest = events.reduce((a, b) => (b.created_at > a.created_at ? b : a));
      return parseArticle(newest);
    },
    enabled: !!identifier,
    staleTime: 2 * 60 * 1000,
  });
}
